import { APP_BASE_HREF } from '@angular/common'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'

import { createEffect, sample } from 'effector'

import { take } from 'rxjs'

import { NodeDefinitionNormalized } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { NewProcessInstance } from '@flowx/designer/shared/entities/process-instance.types'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'

import { BPMNNodeActionsEvents } from './bpmn-node-actions.events'

export class BPMNNodeActionsEffects {
  static readonly navToProcessDefinitionFx = createEffect<
    {
      node: NodeDefinitionNormalized
      openWindow: boolean
    },
    void
  >('navToProcessDefinitionFx')

  static readonly navToEmbeddedInstanceFx = createEffect<
    {
      node: NodeDefinitionNormalized
    },
    void
  >('navToEmbeddedInstanceFx')
}

@Injectable({
  providedIn: 'root',
})
export class BPMNNodeActionsEffectsService {
  #baseHref = inject(APP_BASE_HREF)

  #processInstanceService = inject(ProcessInstanceService)

  private router = inject(Router)

  constructor() {
    BPMNNodeActionsEffects.navToProcessDefinitionFx.use(this.navToProcessDefinition.bind(this))

    sample({
      clock: BPMNNodeActionsEvents.navToProcessDefinition,
      source: {
        node: BpmnEditorStores.selectedNode$,
      },
      fn: ({ node }, { openWindow }) => ({ node, openWindow }),
      target: BPMNNodeActionsEffects.navToProcessDefinitionFx,
    })
  }

  private navToProcessDefinition({
    node,
    openWindow,
  }: {
    node: NodeDefinitionNormalized
    openWindow: boolean
  }): void {
    this.#processInstanceService.applicationProcessData
      .pipe(take(1))
      .subscribe((applicationProcessData) => {
        let workspaceId: string, applicationId: string, applicationVersionId: string
        const resourceId = applicationProcessData.processData.definition.resourceId
        const embeddedSubprocessInstance =
          applicationProcessData.processData.embeddedSubprocessInstance

        if (!embeddedSubprocessInstance) {
          const instance = applicationProcessData.processData
            .instance as unknown as NewProcessInstance
          workspaceId = instance.workspaceId
          applicationId = instance.applicationId
          applicationVersionId = instance.buildApplicationVersionId
        } else {
          workspaceId = embeddedSubprocessInstance.workspaceId
          applicationId = embeddedSubprocessInstance.applicationId
          applicationVersionId = embeddedSubprocessInstance.buildApplicationVersionId
        }

        if (workspaceId && applicationId && applicationVersionId && resourceId) {
          const link = this.router.serializeUrl(
            this.router.createUrlTree(
              [
                this.#baseHref,
                'guard',
                'workspace',
                workspaceId,
                'projects',
                applicationId,
                'config',
                applicationVersionId,
                'processes',
                resourceId,
                'diagram',
              ],
              {
                queryParams: { nodeId: node.id, edit: true },
              }
            )
          )

          if (openWindow) {
            window.open(link, '_blank')
          } else {
            this.router.navigateByUrl(link)
          }
        }
      })
  }
}
